import { Button } from '@clover/web-lib-consumer-ui';
import { Icon } from 'components/Icon';
import { useCart } from 'hooks/context/cart';
import { useCustomer } from 'hooks/context/customer/customer';
import { useGiftCardCart } from 'hooks/context/giftCardCart';
import { useMerchantState } from 'hooks/context/MerchantState';
import { useOrderHistory } from 'hooks/context/OrderHistory';
import { useRunningOrder } from 'hooks/context/RunningOrder';
import { useCanPlaceTableOrder } from 'hooks/context/useCanPlaceTableOrder';
import { useDeliveryPlacementConditions } from 'hooks/useDeliveryPlacementConditions';
import { useSmartNavigate } from 'hooks/useSmartNavigate';
import { FC, memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSubmitOrder } from 'routes/Checkout/context/SubmitOrder/SubmitOrder';
import { useInvoiceSummary } from 'routes/Checkout/context/InvoiceSummary';
import { CART_TEST_IDS } from './Cart.consts';
import styles from './CartBody.module.scss';
import { CartHeader } from './CartHeader';
import { useContactInfoFormState } from './ContactInfoForm';
import { CartLineItems } from './LineItems/CartLineItems';
import { OptionalTextArea } from './OptionalTextArea';
import { ReorderError } from './ReorderError';
import { ScheduledOrderWrapper } from './ScheduledOrderWrapper';
import { CartBanners } from './CartBanners';
import { Spinner } from './Spinner';
import { MODAL_CLOSE_BUTTON_TEST_ID } from 'components/Modal.const';

const MAXIMUM_CHARACTER_LENGTH = 255;

export const CartBody: FC<{ closeCartPreview?: () => void; toggleShowHeader?: () => void }> = memo(function CartBody({
  closeCartPreview,
  toggleShowHeader,
}) {
  const navigate = useSmartNavigate();
  const { t } = useTranslation();
  const { isInvoiceSummaryLoading } = useInvoiceSummary();
  const { showCustomerAuthState, showRunningOrderState } = useMerchantState();
  const { isLoggedIn } = useCustomer();
  const { itemCount: cartItemCount } = useCart();
  const { itemCount: giftCardCount } = useGiftCardCart();
  const shouldAuthenticateCustomer = showCustomerAuthState && !isLoggedIn;
  const [isOpen, setIsOpen] = useState(false);
  const toggleShowScheduledOrderModal = useCallback(() => setIsOpen((current) => !current), []);
  const { clearUnavailableItems, reorderFailureCode } = useOrderHistory();

  const isTableOrderPlaceable = useCanPlaceTableOrder();
  const { submitTableOrder } = useSubmitOrder();
  const { disableDeliverySubmit } = useDeliveryPlacementConditions();

  const { setNote, note } = useContactInfoFormState();
  const runningOrder = useRunningOrder();
  useEffect(() => {
    if (runningOrder.note) {
      setNote(runningOrder.note);
    }
  }, [runningOrder.note, setNote]);

  const itemCount = cartItemCount + giftCardCount;

  const getNextPath = useMemo(() => {
    if (showRunningOrderState) return '~running-order';
    if (shouldAuthenticateCustomer) return '~login/info';

    return '~checkout';
  }, [shouldAuthenticateCustomer, showRunningOrderState]);

  const handleNavigate = useCallback(async () => {
    if (closeCartPreview) {
      closeCartPreview();
    }
    const path = getNextPath;
    const state = shouldAuthenticateCustomer ? { redirectPath: '~checkout' } : undefined;

    navigate(path, {
      replace: true,
      state,
    });
  }, [shouldAuthenticateCustomer, navigate, getNextPath, closeCartPreview]);

  const handleSubmit = useCallback(async () => {
    if (!isTableOrderPlaceable) {
      return;
    }

    try {
      await submitTableOrder();
      handleNavigate();
    } catch (e) {
      console.error('Order Failed', e);
    }
  }, [handleNavigate, isTableOrderPlaceable, submitTableOrder]);

  // In mobile, remove cart header when sliding out
  const slideOpen = useCallback(() => {
    toggleShowHeader?.();
    toggleShowScheduledOrderModal();
  }, [toggleShowHeader, toggleShowScheduledOrderModal]);

  const slideClose = useCallback(() => {
    toggleShowHeader?.();
    toggleShowScheduledOrderModal();
  }, [toggleShowHeader, toggleShowScheduledOrderModal]);

  //we want to dismiss any unavailable item banners on an empty cart
  useEffect(() => {
    if (itemCount === 0) {
      clearUnavailableItems();
    }
  }, [itemCount, clearUnavailableItems]);

  const onBlurButton = () => {
    document.querySelector<HTMLElement>(`[data-testid="${MODAL_CLOSE_BUTTON_TEST_ID}"]`)?.focus();
  };

  if (reorderFailureCode) {
    return (
      <>
        <CartHeader onClick={slideOpen} />
        <ReorderError onClick={closeCartPreview} />
      </>
    );
  }

  return (
    <>
      {!isOpen && (
        <>
          <CartHeader onClick={slideOpen} />
          {isInvoiceSummaryLoading && <Spinner />}
          {!isInvoiceSummaryLoading && (
            <>
              <CartBanners onClick={closeCartPreview} gutters="all" />
              {showRunningOrderState && (
                <div className={styles.specialInstructions}>
                  <OptionalTextArea
                    value={note}
                    onChange={({ target }) => setNote(target.value)}
                    maxLength={MAXIMUM_CHARACTER_LENGTH}
                    data-testid={CART_TEST_IDS.specialInstructions}
                  >
                    {t('contactForm.specialInstructions')}
                  </OptionalTextArea>
                </div>
              )}
              <CartLineItems onClick={closeCartPreview} className={styles.lineItemsWrapper} />
            </>
          )}
          <div className={styles.buttonWrapper}>
            <Button
              type="button"
              className={styles.button}
              onClick={showRunningOrderState ? handleSubmit : handleNavigate}
              data-testid={CART_TEST_IDS.checkout}
              disabled={!itemCount || disableDeliverySubmit}
              onBlur={onBlurButton}
            >
              <span>
                {showRunningOrderState ? t('cart.sendToKitchen', { count: itemCount }) : t('cart.continueToCheckout')}
              </span>
              <Icon className={styles.arrowIcon} name="leftArrow" size={16}></Icon>
            </Button>
          </div>
        </>
      )}
      {isOpen && <ScheduledOrderWrapper onClose={slideClose} />}
    </>
  );
});
